import { useEffect, useRef, useState, type ReactNode, type MouseEvent as ReactMouseEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import PelviaLogo from './PelviaLogo'
import { LEARN_GROUPS, getLearnPage } from '@/pages/learn/learnPages'
import { signInUrl, signUpUrl } from '@/lib/auth'
import './site-header.css'

const Caret = () => (
  <svg className="sh-caret" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m6 9 6 6 6-6" />
  </svg>
)

/* Nav label that bolds on hover without nudging its neighbors: a hidden bold
   copy (::after, via data-text) reserves the bold width up front. */
const NavLabel = ({ text }: { text: string }) => (
  <span className="sh-label" data-text={text}>
    {text}
  </span>
)

/** A hover/click nav dropdown that closes on outside click and route change. */
function NavDropdown({ label, panelClass, children }: { label: string; panelClass?: string; children: ReactNode }) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const location = useLocation()

  useEffect(() => {
    setOpen(false)
  }, [location.pathname, location.hash])

  useEffect(() => {
    if (!open) return
    const onClick = (e: globalThis.MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('click', onClick)
    return () => document.removeEventListener('click', onClick)
  }, [open])

  return (
    <div
      className={`sh-menu${open ? ' open' : ''}`}
      ref={ref}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
    >
      <button
        type="button"
        className="sh-link sh-menu-trigger"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        <NavLabel text={label} />
        <Caret />
      </button>
      <div className={`sh-dropdown${panelClass ? ` ${panelClass}` : ''}`} role="menu">
        {children}
      </div>
    </div>
  )
}

/**
 * Single navigation used across the landing page and every inner page.
 * Section links resolve to the homepage's in-page anchors no matter where you
 * are, the dropdowns expose the depth of the site, and a persistent
 * "Start your intake" CTA keeps the primary action one click away.
 */
export default function SiteHeader() {
  const location = useLocation()
  const navigate = useNavigate()
  const onHome = location.pathname === '/'

  const [scrolled, setScrolled] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60)
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname, location.hash])

  function goToSection(e: ReactMouseEvent, id: string) {
    e.preventDefault()
    setMenuOpen(false)
    if (onHome) {
      const el = document.getElementById(id)
      if (el) {
        const top = el.getBoundingClientRect().top + window.scrollY - 80
        window.scrollTo({ top, behavior: 'smooth' })
      }
    } else {
      navigate(`/#${id}`)
    }
  }

  const solid = !onHome || scrolled

  return (
    <header className={`site-header${onHome ? ' home' : ''}${solid ? ' solid' : ''}${menuOpen ? ' menu-open' : ''}`}>
      <div className="sh-inner">
        <Link to="/" className="sh-brand">
          <PelviaLogo size={34} animation="idle" alt="" />
          <span className="sh-wordmark">Pelvia</span>
        </Link>

        <nav className={`sh-nav${menuOpen ? ' open' : ''}`} aria-label="Primary">
          <a href="/#care-model" className="sh-link" onClick={(e) => goToSection(e, 'care-model')}>
            <NavLabel text="How it works" />
          </a>

          <Link to="/pelviacare" className="sh-link">
            <NavLabel text="PelviaCare+" />
          </Link>

          <NavDropdown label="Learn">
            {LEARN_GROUPS.map((group) => (
              <div key={group.label} className="sh-dropdown-group">
                <span className="sh-dropdown-label">{group.label}</span>
                {group.slugs.map((slug) => {
                  const p = getLearnPage(slug)
                  if (!p) return null
                  return (
                    <Link key={slug} to={p.path} className="sh-dropdown-item" role="menuitem">
                      <span className="sh-dropdown-item-title">{p.shortTitle}</span>
                      <span className="sh-dropdown-item-desc">{p.oneLiner}</span>
                    </Link>
                  )
                })}
              </div>
            ))}
          </NavDropdown>

          <Link to="/security" className="sh-link">
            <NavLabel text="Security" />
          </Link>

          <Link to="/mission" className="sh-link">
            <NavLabel text="Mission" />
          </Link>

          <a href={signInUrl()} className="sh-signin">
            Sign in
          </a>
          <a href={signUpUrl()} className="sh-cta">
            Start your intake
          </a>
        </nav>

        <button
          type="button"
          className={`sh-toggle${menuOpen ? ' open' : ''}`}
          aria-label="Toggle menu"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((o) => !o)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>
    </header>
  )
}
